import {
  createRoute,
  lazyRouteComponent,
  redirect,
} from '@tanstack/react-router';
import { usePortalSessionStore } from './portalSession';
import { rootRoute } from '@/app/routes/roots';
import { clearAccess } from '@/features/Auth/accessState';
import { ensureAccess } from '@/features/Auth/accessSession';
import apiClient from '@/api';

/** Independent client route tree, gated by a server-verified selected grant. */

const portalLoginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: 'portal/login',
  beforeLoad: () => {
    // The protected gate below verifies the cookie and selected client grant.
    if (usePortalSessionStore.getState().isAuthenticated) {
      throw redirect({ to: '/portal', replace: true });
    }
  },
  component: lazyRouteComponent(
    () => import('./views/PortalLoginPage'),
    'PortalLoginPage',
  ),
});

const portalAccessRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: 'portal/access',
  component: lazyRouteComponent(
    () => import('@/features/Auth/views/AccessChooserPage'),
    'AccessChooserPage',
  ),
});

/**
 * The authenticated portal tree.
 *
 * `beforeLoad` proves the session with a real request before any page renders,
 * for the reason the advisor gate spells out at length: `isAuthenticated` is a
 * localStorage guess that outlives the HttpOnly cookie it describes, and
 * admitting somebody on that guess means every child query fires and 401s in
 * parallel while the layout sits on a spinner.
 */
const portalRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: '_portal',
  beforeLoad: async () => {
    if (!usePortalSessionStore.getState().isAuthenticated)
      throw redirect({ to: '/portal/login', replace: true });
    let hasAccess;
    try {
      hasAccess = await ensureAccess();
    } catch (error) {
      const status = (error as { response?: { status?: number } }).response
        ?.status;
      if (status !== 401 && status !== 403) throw error;
      usePortalSessionStore.getState().logout();
      throw redirect({ to: '/portal/login', replace: true });
    }
    if (!hasAccess) throw redirect({ to: '/portal/access', replace: true });
    try {
      await apiClient.get('/portal/me');
    } catch (error) {
      const status = (error as { response?: { status?: number } }).response
        ?.status;
      if (status === 403) {
        clearAccess('portal');
        throw redirect({ to: '/portal/access', replace: true });
      }
      if (status !== 401) throw error;
      usePortalSessionStore.getState().logout();
      throw redirect({ to: '/portal/login', replace: true });
    }
  },
  component: lazyRouteComponent(() => import('./PortalLayout'), 'PortalLayout'),
});

const portalOverviewRoute = createRoute({
  getParentRoute: () => portalRoute,
  path: 'portal',
  component: lazyRouteComponent(
    () => import('./views/PortalOverview'),
    'PortalOverview',
  ),
});

const portalHoldingsRoute = createRoute({
  getParentRoute: () => portalRoute,
  path: 'portal/holdings',
  component: lazyRouteComponent(
    () => import('./views/PortalHoldings'),
    'PortalHoldings',
  ),
});

const portalActivityRoute = createRoute({
  getParentRoute: () => portalRoute,
  path: 'portal/activity',
  validateSearch: (search: { account?: unknown }): { account?: string } => ({
    account: typeof search.account === 'string' ? search.account : undefined,
  }),
  component: lazyRouteComponent(
    () => import('./views/PortalActivity'),
    'PortalActivity',
  ),
});

const portalPlanRoute = createRoute({
  getParentRoute: () => portalRoute,
  path: 'portal/plan',
  component: lazyRouteComponent(
    () => import('./views/PortalPlan'),
    'PortalPlan',
  ),
});

const portalDocumentsRoute = createRoute({
  getParentRoute: () => portalRoute,
  path: 'portal/documents',
  component: lazyRouteComponent(
    () => import('./views/PortalDocuments'),
    'PortalDocuments',
  ),
});

const portalDetailsRoute = createRoute({
  getParentRoute: () => portalRoute,
  path: 'portal/details',
  component: lazyRouteComponent(
    () => import('./views/PortalProfileDetails'),
    'PortalProfileDetails',
  ),
});

const portalFundingRoute = createRoute({
  getParentRoute: () => portalRoute,
  path: 'portal/funding',
  component: lazyRouteComponent(
    () => import('./views/PortalDetails'),
    'PortalFunding',
  ),
});

const portalDisclosuresRoute = createRoute({
  getParentRoute: () => portalRoute,
  path: 'portal/disclosures',
  component: lazyRouteComponent(
    () => import('./views/PortalDetails'),
    'PortalDisclosures',
  ),
});

const portalSoteriaRoute = createRoute({
  getParentRoute: () => portalRoute,
  path: 'portal/ask',
  component: lazyRouteComponent(
    () => import('./views/PortalSoteria'),
    'PortalSoteria',
  ),
});

const portalMessagesRoute = createRoute({
  getParentRoute: () => portalRoute,
  path: 'portal/messages',
  // `?thread=<id>` is what the "an answer is waiting" email links to. Without
  // it that email would land a client on a list and ask them to find the
  // conversation it was telling them about.
  //
  // Unvalidated beyond "a string": the id is handed straight to a request the
  // API scopes to this client's own threads, so a bad one is a 404, not a
  // leak. Rejecting it here would only change which of the two says so.
  // The input type is `{ thread?: unknown }` rather than
  // `Record<string, unknown>` on purpose: TanStack decides whether a `Link`
  // MUST pass search params by asking whether `{}` satisfies this signature.
  // A Record does not, which made every plain `<Link to="/portal/messages">`
  // a type error demanding `search={{ thread: undefined }}`.
  validateSearch: (search: { thread?: unknown }): { thread?: string } => ({
    thread: typeof search.thread === 'string' ? search.thread : undefined,
  }),
  component: lazyRouteComponent(
    () => import('./views/PortalMessages'),
    'PortalMessages',
  ),
});

const portalDocumentationRoute = createRoute({
  getParentRoute: () => portalRoute,
  path: 'portal/documentation',
  validateSearch: (search: { article?: unknown }): { article?: string } => ({
    article: typeof search.article === 'string' ? search.article : undefined,
  }),
  component: lazyRouteComponent(
    () => import('@/features/Knowledge/KnowledgePage'),
    'ClientDocumentationPage',
  ),
});

const portalChangelogRoute = createRoute({
  getParentRoute: () => portalRoute,
  path: 'portal/changelog',
  validateSearch: (search: { article?: unknown }): { article?: string } => ({
    article: typeof search.article === 'string' ? search.article : undefined,
  }),
  component: lazyRouteComponent(
    () => import('@/features/Changelog/ChangelogPage'),
    'ChangelogPage',
  ),
});

const portalHelpDeskRoute = createRoute({
  getParentRoute: () => portalRoute,
  path: 'portal/help',
  component: lazyRouteComponent(
    () => import('./views/PortalUtilities'),
    'PortalHelpDesk',
  ),
});

const portalSettingsRoute = createRoute({
  getParentRoute: () => portalRoute,
  path: 'portal/settings',
  component: lazyRouteComponent(
    () => import('./views/PortalUtilities'),
    'PortalSettings',
  ),
});

const portalIntegrationsRoute = createRoute({
  getParentRoute: () => portalRoute,
  path: 'portal/integrations',
  component: lazyRouteComponent(
    () => import('./views/PortalUtilities'),
    'PortalIntegrations',
  ),
});

export const portalRoutes = [
  portalLoginRoute,
  portalAccessRoute,
  portalRoute.addChildren([
    portalOverviewRoute,
    portalHoldingsRoute,
    portalActivityRoute,
    portalPlanRoute,
    portalDocumentsRoute,
    portalDetailsRoute,
    portalFundingRoute,
    portalDisclosuresRoute,
    portalMessagesRoute,
    portalSoteriaRoute,
    portalDocumentationRoute,
    portalChangelogRoute,
    portalHelpDeskRoute,
    portalSettingsRoute,
    portalIntegrationsRoute,
  ]),
];
